export type OfferSource = 'BB10' | 'CSB10';
export interface OfferDetails {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
}
type OfferField = keyof OfferDetails;
export type OfferResult =
  | { success: true; jobId: string }
  | { success: false; message: string; fieldErrors: Partial<Record<OfferField, string>> };

const fieldNames: Record<string, OfferField> = {
  firstname: 'firstName', lastname: 'lastName', email: 'email', mobile: 'mobile',
};
const unconfirmedMessage = 'We couldn’t confirm your offer request. Please call 01324 636330 for help before submitting again.';

export async function submitOffer(details: OfferDetails, source: OfferSource): Promise<OfferResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch('https://api.thecreamerysportsbar.com/offer/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstname: details.firstName.trim(), lastname: details.lastName.trim(),
        email: details.email.trim(), mobile: details.mobile.trim(), source,
      }),
      signal: controller.signal,
    });
    // Queue/database errors may return HTML, empty bodies or non-standard JSON.
    const body = await response.json().catch(() => null);
    const jobId = body?.job_id;
    const validJobId = (typeof jobId === 'string' && jobId.trim().length > 0)
      || (typeof jobId === 'number' && Number.isSafeInteger(jobId) && jobId > 0);
    if (response.status === 202 && body?.type === 'OFFER' && body?.status === 'queued'
      && validJobId) {
      return { success: true, jobId: String(jobId) };
    }
    const fieldErrors: Partial<Record<OfferField, string>> = {};
    if (response.status === 422 && body?.errors && typeof body.errors === 'object' && !Array.isArray(body.errors)) {
      Object.entries(fieldNames).forEach(([apiName, formName]) => {
        const messages = body.errors[apiName];
        if (Array.isArray(messages)) {
          const message = messages.find((value: unknown) => typeof value === 'string' && value.trim());
          if (message) fieldErrors[formName] = message;
        }
      });
    }
    const message = Object.keys(fieldErrors).length
      ? 'Please check the highlighted details and try again.'
      : response.status === 400 || response.status === 422
        ? 'We couldn’t accept these details. Please check them and try again, or call 01324 636330 for help.'
        : response.status === 404
          ? 'The offer service is currently unavailable. Please try again later or call 01324 636330.'
          : unconfirmedMessage;
    return { success: false, message, fieldErrors };
  } catch {
    return { success: false, message: unconfirmedMessage, fieldErrors: {} };
  } finally {
    clearTimeout(timeout);
  }
}
